import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Breadcrumbs,
  Link,
  useTheme,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { CSVLink } from "react-csv";
import analyticsService from "../../../../services/analyticsService";
import config from "../../../../config";
import { getDateRangeFromFilter } from "../../../../utils/dateFilterUtils";

const TopConvertingProductsDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [productsData, setProductsData] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const dateFilterValue = location.state?.dateFilterValue;
        const dateRange = getDateRangeFromFilter(dateFilterValue);

        const response = await analyticsService.getTopConvertingProducts(
          config.clientId,
          100,
          dateRange.start_date,
          dateRange.end_date
        );

        if (response.success && response.data) {
          setProductsData(response.data);
        } else {
          setError(response.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching top converting products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (config.clientId) {
      fetchData();
    }
  }, [location.state?.dateFilterValue]);

  const filteredData = useMemo(
    () =>
      productsData.filter((row) =>
        row.product_name.toLowerCase().includes(filter.toLowerCase())
      ),
    [filter, productsData]
  );

  const chartState = {
    series: [{ data: filteredData.map((d) => d.total_quantity_sold) }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", horizontal: true } },
      dataLabels: { enabled: false },
      xaxis: { categories: filteredData.map((d) => d.product_name) },
      colors: [theme.palette.primary.main],
    },
  };

  return (
    <Box p={3}>
      <Breadcrumbs sx={{ mb: 2, fontSize: "14px" }}>
        <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => navigate("/analytics")}>
          Analytics
        </Link>
        <Typography color={theme.palette.text.primary}>Top Converting Products</Typography>
      </Breadcrumbs>

      <Box mb={2}>
        <Button variant="outlined" onClick={() => navigate("/analytics")} sx={{ borderRadius: 2 }}>
          ← Back
        </Button>
      </Box>

      {loading ? (
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      ) : error ? (
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography color="error">{error}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <ReactApexChart options={chartState.options} series={chartState.series} type="bar" height={350} />
          </CardContent>
        </Card>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <TextField
          label="Search Product"
          variant="outlined"
          value={filter}
          size="small"
          onChange={(e) => setFilter(e.target.value)}
          sx={{ width: 250 }}
        />

        {filteredData.length > 0 && (
          <Button variant="contained" color="primary">
            <CSVLink data={filteredData} filename={"top_products.csv"} style={{ color: "#fff", textDecoration: "none" }}>
              Export CSV
            </CSVLink>
          </Button>
        )}
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableCell><strong>Product Name</strong></TableCell>
                <TableCell><strong>Quantity Sold</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.product_name}</TableCell>
                      <TableCell>{row.total_quantity_sold}</TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {filteredData.length > 0 && (
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TopConvertingProductsDetails;
